import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  TextField,
  Box,
  MenuItem,
  Alert
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useCreateAdminRoleMutation, useUpdateAdminRoleMutation } from '../../redux/api/adminRolesApi';
import { selectTabs } from '../../redux/slices/tabsSlice';
import { AdminRole } from '../../resources/types/adminRolesTypes';
import { AVAILABLE_ICONS } from './roleIcons';
import TabsSelectionDialog from '../AdminsPage/TabsSelectionDialog';

interface AdminRoleDialogProps extends DialogProps {
  onClose: () => void;
  onSuccess: () => void;
  editRole?: AdminRole | null;
}

const AdminRoleDialog: React.FC<AdminRoleDialogProps> = ({ 
  open, 
  onClose,
  onSuccess,
  editRole 
}) => {
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('Security');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tabsDialogOpen, setTabsDialogOpen] = useState(false);

  const tabsState = useSelector(selectTabs);
  const [createRole, { isLoading: isCreating }] = useCreateAdminRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateAdminRoleMutation();
  
  const isLoading = isCreating || isUpdating;
  const isEditMode = !!editRole;

  const tabs = tabsState.items || [];

  const selectedTabsNames = useMemo(() => {
    return selectedPermissions
      .map(id => {
        const tab = tabs.find(t => t._id === id);
        return tab ? tab.name : null;
      })
      .filter(Boolean)
      .join(', ');
  }, [selectedPermissions, tabs]);

  useEffect(() => {
    if (!open) {
      setTabsDialogOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (editRole) {
        setId(editRole.id || '');
        setName(editRole.name || '');
        setIcon(editRole.icon || 'Security');
        setSelectedPermissions(editRole.tabs?.map(t => t._id) || []);
      } else {
        setId('');
        setName('');
        setIcon('Security');
        setSelectedPermissions([]);
      }
      setError(null);
    }
  }, [open, editRole]);


  const handleSave = useCallback(async () => {
    setError(null);
    
    if (!isEditMode && !id.trim()) {
      setError('Код ролі обов\'язковий');
      return;
    }
    
    if (!name.trim()) {
      setError('Назва ролі обов\'язкова');
      return;
    }

    try {
      if (isEditMode && editRole) {
        const updatePayload = {
          name: name.trim(),
          tabs: selectedPermissions,
          icon: icon
        };
        
        console.log('=== Updating role with payload:', updatePayload);
        await updateRole({ id: editRole._id, body: updatePayload }).unwrap();
        console.log('=== Role updated successfully');
      } else {
        const createPayload = {
          id: id.trim(),
          name: name.trim(),
          tabs: selectedPermissions,
          icon: icon
        };
        
        console.log('=== Creating role with payload:', createPayload);
        await createRole(createPayload).unwrap();
        console.log('=== Role created successfully');
      }
      
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('=== Error saving role:', err);
      const errorMessage = err?.data?.message || err?.message || 
        (isEditMode ? 'Помилка при оновленні ролі' : 'Помилка при створенні ролі');
      setError(errorMessage);
    }
  }, [id, name, icon, selectedPermissions, isEditMode, editRole, createRole, updateRole, onSuccess, onClose]);

  const isFormValid = (isEditMode || id.trim().length > 0) && name.trim().length > 0;

  return (
    <>
    <Dialog fullWidth maxWidth='sm' open={open} onClose={onClose}>
      <DialogTitle>{isEditMode ? 'Редагування ролі' : 'Створення нової ролі'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          fullWidth
          autoFocus={!isEditMode}
          label='Код ролі (ID)'
          margin='normal'
          variant='outlined'
          value={id}
          onChange={(e) => setId(e.target.value)}
          disabled={isLoading || isEditMode}
          placeholder='Наприклад: driver, operator, manager'
          helperText={isEditMode ? 'Код ролі не можна змінити' : 'Унікальний код для ідентифікації ролі'}
        />
        
        <TextField
          fullWidth
          label='Назва ролі'
          margin='normal'
          variant='outlined'
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isLoading}
        />

        <TextField
          fullWidth
          select
          label='Іконка'
          margin='normal'
          variant='outlined'
          value={icon}
          onChange={(e) => setIcon(e.target.value)}
          disabled={isLoading}
        >
          {AVAILABLE_ICONS.map(({ value, label, Icon }) => (
            <MenuItem key={value} value={value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon sx={{ fontSize: 20 }} />
                <span>{label}</span>
              </Box>
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label='Дозволи (Permissions)'
          margin='normal'
          variant='outlined'
          value={selectedTabsNames || 'Не вибрано'}
          onClick={() => setTabsDialogOpen(true)}
          InputProps={{
            readOnly: true,
            style: { cursor: 'pointer' }
          }}
          helperText={`Вибрано: ${selectedPermissions.length} дозволів`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Скасувати
        </Button>
        <Button 
          color='primary' 
          variant='contained'
          onClick={handleSave}
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? 'Збереження...' : (isEditMode ? 'Оновити' : 'Зберегти')}
        </Button>
      </DialogActions>
    </Dialog>
    
    <TabsSelectionDialog
      open={tabsDialogOpen}
      onClose={() => setTabsDialogOpen(false)}
      tabs={tabs}
      selectedTabs={selectedPermissions}
      onSave={(selectedTabs) => {
        setSelectedPermissions(selectedTabs);
        setTabsDialogOpen(false);
      }}
    />
    </>
  );
};

export default AdminRoleDialog;
